import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import mediaLogo from './Media_Logo.png';

const NAV = [
  { to: '/jobs', label: 'Jobs', icon: 'list', roles: ['super_admin', 'manager', 'designer'] },
  { to: '/archive', label: 'Archive', icon: 'archive', roles: ['super_admin', 'manager', 'designer'] },
  { to: '/dashboard', label: 'Dashboard', icon: 'chart', roles: ['super_admin', 'manager'] },
  { to: '/reports', label: 'Reports', icon: 'report', roles: ['super_admin', 'manager'] },
  { to: '/users', label: 'Users', icon: 'users', roles: ['super_admin'] },
  { to: '/clients', label: 'Clients', icon: 'client', roles: ['super_admin'] },
  { to: '/categories', label: 'Categories', icon: 'grid', roles: ['super_admin'] },
  { to: '/statuses', label: 'Statuses', icon: 'clock', roles: ['super_admin'] },
];

const ICONS = {
  list: <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2" rx="1" /><rect x="1" y="7" width="14" height="2" rx="1" /><rect x="1" y="12" width="9" height="2" rx="1" /></svg>,
  archive: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="1" y="4" width="14" height="10" rx="1" /><path d="M1 4l2-3h10l2 3" /><line x1="6" y1="8" x2="10" y2="8" /></svg>,
  chart: <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="8" width="3" height="7" /><rect x="6" y="5" width="3" height="10" /><rect x="11" y="2" width="3" height="13" /></svg>,
  report: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3"><path d="M3 1.5h7l2.5 2.5V14a.5.5 0 01-.5.5H3a.5.5 0 01-.5-.5V2a.5.5 0 01.5-.5z" /><path d="M10 1.5V4h2.5M4 6h5M4 8.5h8M4 11h8" /></svg>,
  users: <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="5" r="3" /><path d="M2 14c0-3.3 2.7-5 6-5s6 1.7 6 5H2z" /></svg>,
  client: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="1" y="5" width="14" height="10" rx="1" /><path d="M5 5V3a3 3 0 016 0v2" /></svg>,
  grid: <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1" /><rect x="9" y="1" width="6" height="6" rx="1" /><rect x="1" y="9" width="6" height="6" rx="1" /><rect x="9" y="9" width="6" height="6" rx="1" /></svg>,
  clock: <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4"><circle cx="8" cy="8" r="6" /><polyline points="8,5 8,8 10,10" /></svg>,
};

const s = {
  sidebar: { width: 220, background: '#fff', borderRight: '1px solid #e9ecef', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  logo: { padding: '16px 14px 12px', borderBottom: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 10 },
  logoTxt: { fontSize: 11, fontWeight: 700, color: '#0f74b9', letterSpacing: '.5px', lineHeight: 1.3 },
  logoSub: { fontSize: 9, color: '#6c757d' },
  navSec: { padding: '8px 14px 3px', fontSize: 10, fontWeight: 600, color: '#6c757d', letterSpacing: '.8px', textTransform: 'uppercase' },
  navItem: { display: 'flex', alignItems: 'center', gap: 9, padding: '8px 14px', fontSize: 13, color: '#343a40', borderLeft: '3px solid transparent', textDecoration: 'none', cursor: 'pointer' },
  active: { background: '#e8f3fb', color: '#0f74b9', fontWeight: 600, borderLeftColor: '#0f74b9' },
  userBox: { padding: '10px 14px', borderTop: '1px solid #e9ecef', display: 'flex', alignItems: 'center', gap: 9, marginTop: 'auto' },
  avatar: { width: 30, height: 30, borderRadius: '50%', background: '#0f74b9', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
};

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = user?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const visible = NAV.filter(n => n.roles.includes(user?.role));
  const mainNav = visible.filter(n => ['/jobs', '/archive', '/dashboard', '/reports'].includes(n.to));
  const adminNav = visible.filter(n => ['/users', '/clients', '/categories', '/statuses'].includes(n.to));

  return (
    <div style={s.sidebar}>
      <div style={s.logo}>
        <img
          src={mediaLogo}
          alt="Delta Media"
          style={{ height: 36, width: 'auto', maxWidth: 44, flexShrink: 0, objectFit: 'contain', display: 'block' }}
        />
        <div>
          <div style={s.logoTxt}>Delta Media</div>
          <div style={s.logoSub}>Creative Management System</div>
        </div>
      </div>

      <nav style={{ flex: 1, padding: '8px 0', overflowY: 'auto' }}>
        <a 
          href={process.env.REACT_APP_MASTER_PORTAL_URL || "http://localhost:5173"} 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 9, 
            padding: '12px 14px', 
            fontSize: 13, 
            color: '#0f74b9', 
            textDecoration: 'none', 
            fontWeight: 'bold',
            background: '#f0f9ff',
            borderBottom: '1px solid #e0f2fe',
            marginBottom: 8
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
          Master Portal
        </a>
        <div style={s.navSec}>Main</div>
        {mainNav.map(n => (
          <NavLink key={n.to} to={n.to} style={({ isActive }) => ({ ...s.navItem, ...(isActive ? s.active : {}) })}>
            {ICONS[n.icon]}{n.label}
          </NavLink>
        ))}
        {adminNav.length > 0 && (
          <>
            <div style={s.navSec}>Admin</div>
            {adminNav.map(n => (
              <NavLink key={n.to} to={n.to} style={({ isActive }) => ({ ...s.navItem, ...(isActive ? s.active : {}) })}>
                {ICONS[n.icon]}{n.label}
              </NavLink>
            ))}
          </>
        )}
      </nav>

      <div style={s.userBox}>
        <div style={s.avatar}>{initials}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#343a40', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</div>
          <div style={{ fontSize: 10, color: '#6c757d' }}>{user?.role?.replace('_', ' ')}</div>
        </div>
        <button type="button" onClick={() => { logout(); navigate('/login'); }}
          title="Logout"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6c757d', padding: 4 }}>
          <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M6 2H2v12h4M11 11l3-3-3-3M14 8H6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
